import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Camera, MediaResult, MediaType, MediaTypeSelection } from '@capacitor/camera';
import Compressor from 'compressorjs';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { TranslationService } from 'src/app/i18n/translation.service';
import { NativeShareService } from 'src/app/commons/services/native-share.service';
import { setPasteTargetArmed } from './paste-arm';

export interface MediaCompressOptions {
  quality: number;
  maxWidth: number;
  maxHeight: number;
}

const DEFAULT_COMPRESS_OPTIONS: MediaCompressOptions = { quality: 0.72, maxWidth: 1800, maxHeight: 1800 };

/**
 * Reusable capture trigger: Take Photo / Record Video (duration-capped) / Choose from Gallery
 * live behind the split-button dropdown; the button body itself reproduces the plain
 * file-browse behavior every upload surface already had, so dropping this in is a pure
 * addition, not a change to existing upload flows.
 */
@Component({
  selector: 'app-media-capture-button',
  standalone: true,
  imports: [CommonModule, SplitButtonModule, ButtonModule],
  templateUrl: './media-capture-button.component.html',
  styleUrl: './media-capture-button.component.css',
})
export class MediaCaptureButtonComponent implements OnInit, OnDestroy {
  private readonly toastService = inject(ToastMessagesService);
  private readonly translationService = inject(TranslationService);
  private readonly zone = inject(NgZone);
  private readonly nativeShare = inject(NativeShareService);

  /**
   * Paste target (SHARE_TO_LUMBERLINQ_PLAN.md, section 4). Many of these buttons can sit on one page
   * (one per document type), so a paste goes ONLY to the button the user last clicked/focused —
   * never to all of them, and never to a guessed one. Any click elsewhere on the page clears it, so
   * a later Ctrl+V in a normal text field is left completely alone.
   */
  private static activeTarget: MediaCaptureButtonComponent | null = null;
  private static listenersReady = false;

  /**
   * Take Photo / Record Video / Choose from Gallery only make sense on native mobile
   * (Android/iOS — live camera + gallery pickers) — web keeps the exact plain "Browse Files"
   * button that was there before, so desktop drag-and-drop/file-picker upload flows are
   * completely untouched. iOS additionally requires NSCameraUsageDescription/
   * NSMicrophoneUsageDescription/NSPhotoLibraryUsageDescription in Info.plist (see
   * codemagic.yaml) or Camera.* calls crash outright — not just a runtime permission prompt.
   */
  readonly showCaptureOptions = Capacitor.isNativePlatform();

  @Input() label = 'Add File';
  @Input() buttonIcon = 'pi pi-paperclip';
  /** Passed straight through to the "Browse Files" hidden input's accept attribute. */
  @Input() acceptTypes = '*/*';
  @Input() multiple = true;
  @Input() disabled = false;
  /** Hide "Record Video" from the dropdown for photo-only surfaces (e.g. product photos). */
  @Input() allowVideo = true;
  /** Set false when the consumer already runs its own compression pass downstream (avoids double-compressing). */
  @Input() compress = true;
  @Input() compressOptions: MediaCompressOptions = DEFAULT_COMPRESS_OPTIONS;
  @Input() maxVideoSeconds = 30;

  /** Every capture path (photo/video/gallery/browse) emits here as plain Files. */
  @Output() filesSelected = new EventEmitter<File[]>();

  @ViewChild('browseInput') browseInputRef!: ElementRef<HTMLInputElement>;

  menuItems: MenuItem[] = [];

  private langSub?: Subscription;

  ngOnInit(): void {
    MediaCaptureButtonComponent.ensureGlobalListeners();
    if (!this.showCaptureOptions) return;
    this.buildMenu();
    this.langSub = this.translationService.language$.subscribe(() => this.buildMenu());
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    if (MediaCaptureButtonComponent.activeTarget === this) MediaCaptureButtonComponent.setActive(null);
  }

  /** Single place that changes the armed button; also tells the app-wide paste sheet to stay out of the way (paste-arm.ts). */
  private static setActive(target: MediaCaptureButtonComponent | null): void {
    MediaCaptureButtonComponent.activeTarget = target;
    setPasteTargetArmed(target !== null);
  }

  /** One page-level listener for every instance: a click outside any capture button clears the paste target. */
  private static ensureGlobalListeners(): void {
    if (MediaCaptureButtonComponent.listenersReady || typeof document === 'undefined') return;
    MediaCaptureButtonComponent.listenersReady = true;
    document.addEventListener('pointerdown', (e) => {
      const t = e.target as Element | null;
      if (!t?.closest?.('app-media-capture-button')) MediaCaptureButtonComponent.setActive(null);
    }, true);
  }

  @HostListener('pointerdown')
  @HostListener('focusin')
  markAsPasteTarget(): void {
    if (!this.disabled) MediaCaptureButtonComponent.setActive(this);
  }

  /**
   * Ctrl+V / Cmd+V with FILES on the clipboard (PDF, Word, Excel, images copied in Explorer/Finder,
   * screenshots...). Text-only paste is never touched. If this button is not the one the user just
   * used, or is disabled, the event is ignored so nothing else is affected.
   */
  @HostListener('document:paste', ['$event'])
  onDocumentPaste(event: ClipboardEvent): void {
    if (MediaCaptureButtonComponent.activeTarget !== this || this.disabled) return;
    const pasted = Array.from(event.clipboardData?.files ?? []);
    if (!pasted.length) return;
    event.preventDefault();
    this.zone.run(() => void this.handlePastedFiles(pasted));
  }

  /** 2026-10-02: a phone or tablet in a browser or in the iPhone app (no native ShareReceiver plugin): the web clipboard can hand over IMAGES
   *  (screenshots, copied photos). It needs a tap (this menu item) and the browser's own permission; iPhone shows its small "Paste" bubble.
   *  Copied PDF / Word / Excel files cannot be read this way (the web clipboard has no files): those need the native plugin. */
  private get canWebPaste(): boolean {
    try {
      return !this.nativeShare.available
        && typeof navigator !== 'undefined' && !!navigator.clipboard && typeof navigator.clipboard.read === 'function'
        && window.isSecureContext
        && typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;   // a desktop has Ctrl+V
    } catch {
      return false;
    }
  }

  private async readWebClipboardImages(): Promise<File[]> {
    const items = await navigator.clipboard.read();
    const files: File[] = [];
    for (const item of items) {
      const type = item.types.find(x => x.startsWith('image/'));
      if (!type) continue;
      const blob = await item.getType(type);
      const ext = (type.split('/')[1] || 'png').split('+')[0];
      files.push(new File([blob], `pasted-${Date.now()}-${files.length + 1}.${ext}`, { type }));
    }
    return files;
  }

  /** "Paste" in the phone menu: files sitting on the phone clipboard (native plugin: copied in Files/Drive...), or images (web clipboard). */
  async pasteFromClipboard(): Promise<void> {
    try {
      if (!this.nativeShare.available) {
        const images = await this.readWebClipboardImages();
        if (!images.length) {
          this.toastService.showWarningMessage(
            this.translationService.instant('mediaCapture.pasteNothingTitle'),
            this.translationService.instant('mediaCapture.pasteNoImage'),
          );
          return;
        }
        await this.handlePastedFiles(images);
        return;
      }
      const { files, textOnly } = await this.nativeShare.readClipboard();
      if (!files.length) {
        this.toastService.showWarningMessage(
          this.translationService.instant('mediaCapture.pasteNothingTitle'),
          this.translationService.instant(textOnly ? 'mediaCapture.pasteTextOnly' : 'mediaCapture.pasteNothing'),
        );
        return;
      }
      await this.handlePastedFiles(files);
    } catch (e: any) {
      // the person declined the browser's paste permission / bubble: nothing to report
      if (e?.name === 'NotAllowedError') return;
      this.toastService.showErrorMessage(
        this.translationService.instant('mediaCapture.captureFailedTitle'),
        this.translationService.instant('mediaCapture.captureFailedDetail'),
      );
    }
  }

  private async handlePastedFiles(pasted: File[]): Promise<void> {
    const allowed = pasted.filter(f => this.matchesAccept(f));
    if (allowed.length < pasted.length) {
      this.toastService.showWarningMessage(
        this.translationService.instant('mediaCapture.pasteRejectedTitle'),
        this.translationService.instant('mediaCapture.pasteRejectedDetail'),
      );
    }
    const chosen = this.multiple ? allowed : allowed.slice(0, 1);
    if (!chosen.length) return;
    const processed = await Promise.all(chosen.map(f => this.compressIfImage(f)));
    this.filesSelected.emit(processed);
  }

  /** Same rule as the browser's own file picker `accept` attribute: extensions, mime types, `image/*`. */
  private matchesAccept(file: File): boolean {
    const rules = (this.acceptTypes || '*/*').split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
    if (!rules.length || rules.includes('*/*')) return true;
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    return rules.some(r =>
      r.startsWith('.') ? name.endsWith(r)
        : r.endsWith('/*') ? type.startsWith(r.slice(0, -1))
        : type === r);
  }

  private buildMenu(): void {
    const t = (key: string) => this.translationService.instant(key);
    this.menuItems = [
      { label: t('mediaCapture.takePhoto'), icon: 'pi pi-camera', command: () => this.takePhoto() },
      ...(this.allowVideo
        ? [{ label: t('mediaCapture.recordVideo'), icon: 'pi pi-video', command: () => this.recordVideo() }]
        : []),
      { label: t('mediaCapture.chooseGallery'), icon: 'pi pi-image', command: () => this.chooseFromGallery() },
      // Native app with the ShareReceiver plugin (Android now; iPhone once its plugin is built): files. A phone browser or the iPhone app
      // without it: images from the web clipboard (canWebPaste).
      ...(this.nativeShare.available || this.canWebPaste
        ? [{ label: t('mediaCapture.paste'), icon: 'pi pi-clipboard', command: () => this.pasteFromClipboard() }]
        : []),
    ];
  }

  /** Main split-button click (not the dropdown arrow) — same as the old plain upload button. */
  onDefaultAction(): void {
    this.browseFiles();
  }

  browseFiles(): void {
    this.browseInputRef?.nativeElement.click();
  }

  async takePhoto(): Promise<void> {
    try {
      const result = await Camera.takePhoto({ quality: 85 });
      const file = await this.mediaResultToFile(result);
      if (file) this.filesSelected.emit([await this.compressIfImage(file)]);
    } catch (err) {
      this.handleCaptureError(err);
    }
  }

  async chooseFromGallery(): Promise<void> {
    try {
      const { results } = await Camera.chooseFromGallery({ mediaType: MediaTypeSelection.Photo });
      const files = (await Promise.all(results.map(r => this.mediaResultToFile(r))))
        .filter((f): f is File => !!f);
      const compressed = await Promise.all(files.map(f => this.compressIfImage(f)));
      if (compressed.length) this.filesSelected.emit(compressed);
    } catch (err) {
      this.handleCaptureError(err);
    }
  }

  async recordVideo(): Promise<void> {
    try {
      const result = await Camera.recordVideo({ includeMetadata: true });
      const duration = result.metadata?.duration;
      if (duration !== undefined && duration > this.maxVideoSeconds) {
        this.toastService.showWarningMessage(
          this.translationService.instant('mediaCapture.videoTooLongTitle'),
          this.translationService.instant('mediaCapture.videoTooLongDetail'),
        );
        return;
      }
      const file = await this.mediaResultToFile(result);
      if (file) this.filesSelected.emit([file]);
    } catch (err) {
      this.handleCaptureError(err);
    }
  }

  private async mediaResultToFile(result: MediaResult): Promise<File | null> {
    const path = result.webPath || result.uri;
    if (!path) return null;
    const isVideo = result.type === MediaType.Video;
    const ext = (result.metadata?.format || (isVideo ? 'mp4' : 'jpeg')).replace('jpeg', 'jpg');
    const blob = await (await fetch(path)).blob();
    const mime = blob.type || `${isVideo ? 'video' : 'image'}/${ext}`;
    return new File([blob], `capture_${Date.now()}.${ext}`, { type: mime });
  }

  private handleCaptureError(err: any): void {
    // User backing out of the native camera/gallery picker throws too — not a real error.
    if (err?.message && !/cancel/i.test(err.message)) {
      this.toastService.showErrorMessage(
        this.translationService.instant('mediaCapture.captureFailedTitle'),
        this.translationService.instant('mediaCapture.captureFailedDetail'),
      );
    }
  }

  async onBrowseFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) return;
    const processed = await Promise.all(files.map(f => this.compressIfImage(f)));
    this.filesSelected.emit(processed);
  }

  /** Video isn't transcoded (no ffmpeg.wasm in-app) — only images get shrunk before upload. */
  private compressIfImage(file: File): Promise<File> {
    if (!this.compress || !file.type.startsWith('image/')) {
      return Promise.resolve(file);
    }
    return new Promise((resolve) => {
      new Compressor(file, {
        quality: this.compressOptions.quality,
        maxWidth: this.compressOptions.maxWidth,
        maxHeight: this.compressOptions.maxHeight,
        // Force JPEG out. Without this Compressor defaults to keeping the ORIGINAL mime type,
        // and Camera.chooseFromGallery() is a NATIVE picker -- it does not get iOS Safari's
        // automatic HEIC->JPEG conversion that a plain <input type=file> would, so an iPhone
        // photo can arrive as image/heic. Nothing downstream can display HEIC (Excel cannot
        // embed it, Chrome cannot decode it), and on Android the WebView cannot even decode it,
        // so `error:` below hands back the untouched .heic. Asking for JPEG converts it wherever
        // the platform can decode it, and guarantees a predictable output type everywhere else.
        mimeType: 'image/jpeg',
        success: (compressedFile) => resolve(compressedFile as File),
        error: () => resolve(file),
      });
    });
  }
}
